import React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Hero, { heroImgStyles } from '~components/Hero';
import Text, { ConstrainedText } from '~components/Text';
import FadedImage, { fadedStyle, fadedImageStyle } from '~components/FadedImage';
import Panel from '~components/Panel';

// Page Navigation
import PageNav from '~components/PageNav';
import NewTherapiesCard from '~components/PageCard/NewTherapiesCard';
import DiseaseModifyingCard from '~components/PageCard/DiseaseModifyingCard';

import { roundedImg } from '~theme';

const CellReplacementPage: React.FC = () => {
  return (
    <Layout>
      <SEO title="Les progrès canadiens en matière de recherche de remplacement cellulaire" desc="Description" />
      <Hero title="Les progrès canadiens en matière de recherche de remplacement cellulaire">
        <StaticImage style={heroImgStyles} src="../../images/cell-replacement.jpg" alt="" />
      </Hero>
      <div tw="container">
        <ConstrainedText>
        Sernova, une société de médecine régénératrice de stade clinique basée à London, Ontario, Canada, continue de révéler des résultats prometteurs quant à l’étude clinique financée par FRDJ sur sa thérapie de remplacement cellulaire, à l’aide du <i>Cell Pouch System™</i>. Ce système montre qu’il est en mesure de produire de l’insuline chez les personnes atteintes du diabète de type 1 (DT1) et de favoriser le maintien de la glycémie. L’étude révèle désormais que le maintien du bénéfice clinique chez les participants à l’étude avec le diabète de type 1 en plus d’une sécurité continue et d’une tolérance du système. L’étude fera l’objet d’un second examen indépendant en 2021. 
        </ConstrainedText>
        <ConstrainedText>
        La technologie <i>Cell Pouch System™</i> implique des cellules d’ilots purifiés (cellules qui produisent de l’insuline et qui sont détruites chez les personnes vivant avec DT1) transplantés dans la pochette de cellules (Cell Pouch™) un appareil médical implantable qui fournit un environnement vascularisé pour les cellules thérapeutiques. Il s’agit d’une thérapie de remplacement cellulaire qui a le potentiel de devenir un traitement pour le DT1. Les thérapies de remplacement cellulaire visent à remplacer les cellules bêta perdues ou endommagées par des cellules qui produisent de l’insuline chez les personnes avec le DT1. Dans le cadre de l’étude clinique de Sernova en cours, les participants doivent prendre des médicaments immunosuppresseurs afin de prévenir le rejet de ces cellules implantées. 
        </ConstrainedText>
      </div>
      <FadedImage
        image={
          <StaticImage
            src="../../images/molecules.jpg"
            alt=""
            layout="fullWidth"
            style={fadedStyle}
            imgStyle={fadedImageStyle}
          />
        }
      >
        <Text>
        La détection du peptide C (un biomarqueur de la production d’insuline) dans le flux sanguin des premiers participants ayant subi la transplantation a représenté un succès important de cette étude. 
        </Text>
      </FadedImage>
      <div tw="container">
        <ConstrainedText>
        Sernova planifie également de développer une source illimitée de cellules produisant de l’insuline immunitaire protectrice locale — supprimant ainsi le besoin de médicaments immunosuppresseurs. 
        </ConstrainedText>
        <ConstrainedText>
        L’objectif global de cette clinique est d’évaluer la sécurité, la tolérabilité et l’efficacité de la pochette de cellules chez les personnes vivant avec le DT1. Afin d’y participer, les candidats doivent être atteints du DT1 depuis longtemps, et de subir des épisodes sévères de non-perception de l’hypoglycémie en plus d’avoir aucun peptide C stimulé par le glucose, pour ne nommer que quelques critères. 
        </ConstrainedText>
        <ConstrainedText>
        Le chercheur en tête de l’étude, enquêteur clinique principal, le Dr Piotr Witkowski MD, PhD, éminent spécialiste dans la transplantation d’ilots, nous fournit une mise à jour sur les premiers patients ayant subi une transplantation dont on parlera plus tard et qui ont reçu une deuxième transplantation. Ces patients profitent d’un bénéfice additionnel de cette transplantation, y compris une importante réduction du niveau d’insuline requis à injecter quotidiennement, de même que : 
        </ConstrainedText>
        <ConstrainedText>
          <ul tw="ml-6 list-outside list-disc pl-4">
            <li>Absence d’incidents d’hypoglycémie sévère menaçant la vie</li>
            <li>Maintien de la concentration sanguine de peptide C</li>
            <li>Réduction du dosage d’HbA1c (mesure du contrôle de glucose à long terme)</li>
            <li>
            Amélioration du contrôle du glucose mesuré (p. ex. plage horaire de la glycémie) et du suivi du glucose en continu (CGM) 
            </li>
          </ul>
        </ConstrainedText>
        <ConstrainedText>
        Un participant ayant reçu une transplantation n’a pu qu’à utiliser de l’insuline depuis neuf mois et le contrôle de son glucose est optimal. 
        </ConstrainedText>
        <ConstrainedText>
        Le financement des thérapies de remplacement cellulaire est l’une des initiatives les plus importantes de FRDJ à l’échelle globale et grâce à nos organisations partenaires, nous avons investi plus de 140 millions $ (US) jusqu’ici. 
        </ConstrainedText>
      </div>
      <Panel variant="blue">
        <div tw="grid gap-6 items-center lg:(grid-cols-2)">
          <div tw="lg:order-2 max-w-2xl">
            <StaticImage src="../../images/microscope.jpg" alt="" style={roundedImg} />
          </div>
          <div tw="lg:order-1">
            <Text tw="text-2xl">
            Cette étude représente un autre exemple des incroyables innovations canadiennes quant à la recherche sur le diabète qui accélère la découverte de guérisons. 
            </Text>
          </div>
        </div>
      </Panel>
      <div tw="container">
        <PageNav previous={<NewTherapiesCard />} next={<DiseaseModifyingCard />} />
      </div>
    </Layout>
  );
};

export default CellReplacementPage;
